'use client'

import { useResetPasswordMutation } from '@/hooks/react-query/auth'
import { zodResolver } from '@hookform/resolvers/zod'
import { Eye, EyeOff } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { useTurnstile } from '@/hooks/useTurnstile'
import { toast } from '../common/toast'
import { Button } from '../ui/button'
import { Input } from '../ui/input'

type Props = {
  forgotCode: string
  onClose?: () => void
  onSuccess?: () => void
  isPage?: boolean
}

export const ResetPasswordForm = ({
  forgotCode,
  onClose,
  onSuccess,
  isPage,
}: Props) => {
  const router = useRouter()
  const t = useTranslations('login')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const { TurnstileWidget, isReady, verify, reset } = useTurnstile({
    action: 'reset_password',
  })

  const formSchema = z
    .object({
      password: z
        .string()
        .min(1, t('required_password'))
        .min(6, t('password_too_short')),
      confirmPassword: z.string().min(1, t('required_confirm_password')),
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t('password_not_match'),
      path: ['confirmPassword'],
    })

  const { mutateAsync: resetPassword, isPending } = useResetPasswordMutation()
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    mode: 'onChange',
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  })

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      const turnstileRes = await verify()
      if (!turnstileRes.success) {
        toast.message({
          title: t('error'),
          message: 'Vui lòng xác thực robot',
        })
        reset()
        return
      }

      const res = await resetPassword({
        password: values.password,
        forgot_code: forgotCode,
      })

      if (!res.state) {
        toast.message({
          title: t('fail'),
          message: res.message || t('reset_fail'),
        })
        return
      }

      // Nếu lỗi là 100002 thì chuyển hướng đến trang lỗi
      if (res.error_code === '100002') {
        router.push(`/change-password/${forgotCode}`)
      }

      toast.message({
        title: t('success'),
        message: t('reset_success'),
      })

      if (isPage) router.push('/login')
      onClose?.()
      onSuccess?.()
    } catch (error) {
      console.error(error)
      toast.message({
        title: t('fail'),
        message: (error as Error).message || t('reset_fail_try_again'),
      })
    } finally {
      reset()
    }
  }

  return (
    <>
      <h3 className='font-criteriacapitalize text-heading-5 mb-4 text-center font-medium tracking-normal text-neutral-900'>
        {t('reset_password')}
      </h3>
      <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
        <Input
          label={t('password')}
          className='w-full'
          floating
          autoFocus
          type={showPassword ? 'text' : 'password'}
          error={form.formState.errors?.password}
          {...form.register('password')}
          suffix={
            <button
              type='button'
              tabIndex={-1}
              onClick={() => setShowPassword(!showPassword)}
              className='flex h-6 w-6 items-center justify-center focus:outline-none'
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
        />

        <Input
          label={t('confirm_password')}
          className='w-full'
          floating
          type={showConfirmPassword ? 'text' : 'password'}
          error={form.formState.errors?.confirmPassword}
          {...form.register('confirmPassword')}
          suffix={
            <button
              type='button'
              tabIndex={-1}
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className='flex h-6 w-6 items-center justify-center focus:outline-none'
            >
              {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          }
        />

        <div className='flex justify-center'>{TurnstileWidget}</div>

        <Button
          className='mt-6 w-full normal-case'
          variant='black'
          disabled={!form.formState.isValid || !isReady || isPending}
          loading={isPending}
        >
          {t('reset_password')}
        </Button>
      </form>
    </>
  )
}
